import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getScenariosGraphql } from "../api/services/scenarioGraphqlService";
import { resolveFreshness } from "../components/scenarios/ScenarioFreshnessBadge";
import { useApp } from "../contexts/AppContext";
import { useScenarios } from "./useScenarios";

export interface ScenarioRefreshStatus {
  /** Scenarios currently being refreshed (freshness === "Updating"). */
  updating: { id: string; name: string }[];
  /** Count of scenarios currently updating. */
  count: number;
}

const POLL_INTERVAL_MS = 3_000;

/**
 * Tracks scenarios whose derived freshness is "Updating" to drive the scenario
 * refresh banner. Page-scoped — pass `enabled: false` off the Scenarios /
 * Scenario detail pages so nothing polls globally.
 *
 * Live mode: polls the GraphQL `Updating` view, but ONLY while at least one
 * scenario is updating (the interval turns itself off once the count reaches 0).
 * Demo mode: derives entirely from the in-memory scenario state — no network.
 */
export function useScenarioRefreshStatus(
  enabled: boolean,
): ScenarioRefreshStatus {
  const { mode } = useApp();
  const isDemo = mode === "demo";
  const { scenarios } = useScenarios();

  // Demo: derive directly from context state (manual refresh flips freshness).
  const demoUpdating = useMemo(() => {
    if (!isDemo) return [];
    return scenarios
      .filter((s) => resolveFreshness(s) === "updating")
      .map((s) => ({ id: s.id, name: s.name }));
  }, [isDemo, scenarios]);

  // Live: poll the Updating view. Only fetch on the scenario pages, and only
  // keep polling while the count is > 0 (refetchInterval returns false at 0).
  const liveQuery = useQuery({
    queryKey: ["updatingScenarios"],
    queryFn: () => getScenariosGraphql({ freshness: { eq: "Updating" } }, 200),
    enabled: enabled && !isDemo,
    refetchInterval: (query) => {
      const count = query.state.data?.edges.length ?? 0;
      return count > 0 ? POLL_INTERVAL_MS : false;
    },
    refetchOnWindowFocus: true,
  });

  const liveUpdating = useMemo(() => {
    if (isDemo || !liveQuery.data) return [];
    return liveQuery.data.edges.map((edge) => ({
      id: String(edge.node.id),
      name: edge.node.name,
    }));
  }, [isDemo, liveQuery.data]);

  const updating = isDemo ? demoUpdating : liveUpdating;

  return { updating, count: updating.length };
}
